import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AudiencesClient,
  MessagesClient,
  StreamsClient,
  type AudienceExpression,
} from "../api/generated";
import { AudienceBuilder, AudiencePreviewPanel } from "../components/AudienceBuilder";
import { Alert, Chip, Field, Panel, describeError, useAsync } from "../components/ui";

const audiences = new AudiencesClient("");
const messages = new MessagesClient("");
const streams = new StreamsClient("");

/**
 * Console module: <b>Compose &amp; broadcast</b>.
 *
 * One message, addressed to an audience, read by many. What this screen assembles is a single
 * publish — regardless of whether it reaches one site or a thousand (§2.1).
 */
export function ComposePage() {
  const navigate = useNavigate();
  const schema = useAsync(() => audiences.audiences_Schema());
  const streamList = useAsync(() => streams.streams_List());

  const [stream, setStream] = useState("");
  const [subject, setSubject] = useState("");
  const [severity, setSeverity] = useState("info");
  const [flags, setFlags] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [payload, setPayload] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [requiresAck, setRequiresAck] = useState(false);
  const [allowsReply, setAllowsReply] = useState(false);
  const [channels, setChannels] = useState<string[]>([]);
  const [audience, setAudience] = useState<AudienceExpression>({ include: [], require: [], exclude: [] });

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const selectedStream = streamList.data?.find((s) => s.key === stream);
  const subjects = selectedStream?.subjects ?? [];

  /**
   * The console defaults expires_at to due_at plus a grace period whenever a due date is set, so
   * the overdue window exists by default rather than by accident (§5.4).
   */
  function onDueChanged(value: string) {
    setDueAt(value);
    if (value && !expiresAt) {
      const grace = new Date(value);
      grace.setDate(grace.getDate() + 3);
      setExpiresAt(grace.toISOString().slice(0, 16));
    }
  }

  async function publish() {
    setBusy(true);
    setError(null);
    try {
      let parsedPayload: unknown = undefined;
      if (payload.trim().length > 0) {
        try {
          parsedPayload = JSON.parse(payload);
        } catch {
          setError("The payload is not valid JSON.");
          setBusy(false);
          return;
        }
      }

      const result = await messages.messages_Publish({
        stream,
        subject,
        severity,
        flags,
        dueAt: dueAt ? new Date(dueAt).toISOString() : undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
        requiresAcknowledgement: requiresAck,
        allowsReply,
        audience,
        channels,
        payload: parsedPayload as never,
        presentation: (title || body) ? { title, body, actions: [] } : undefined,
        attachments: [],
      });

      navigate(`/read-tracking/${result.id}`);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  const ready = stream && subject && (audience.include ?? []).length > 0;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Compose &amp; broadcast</h1>
          <p className="sub">
            Publish one message to an audience. Check the preview before publishing — it lists
            exactly who will receive it.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      <div className="grid-2">
        <div>
          <Panel title="Message">
            <div className="row">
              <Field label="Stream">
                <select value={stream} onChange={(e) => { setStream(e.target.value); setSubject(""); }}>
                  <option value="">Choose…</option>
                  {streamList.data?.map((s) => <option key={s.key} value={s.key}>{s.name} ({s.key})</option>)}
                </select>
              </Field>

              <Field label="Subject">
                {subjects.length > 0 ? (
                  <select value={subject} onChange={(e) => setSubject(e.target.value)}>
                    <option value="">Choose…</option>
                    {subjects.map((s) => (
                      <option key={s.subject} value={s.subject}>{s.subject} ({s.policy})</option>
                    ))}
                  </select>
                ) : (
                  <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="bookings.deadline.extended" />
                )}
              </Field>

              <Field label="Severity">
                <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                  <option value="info">info</option>
                  <option value="warning">warning</option>
                  <option value="critical">critical</option>
                </select>
              </Field>
            </div>

            <Field label="Title"><input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
            <Field label="Body"><textarea value={body} onChange={(e) => setBody(e.target.value)} /></Field>

            <Field label="Payload — JSON, optional (required by strict subjects)">
              <textarea
                className="mono"
                value={payload}
                onChange={(e) => setPayload(e.target.value)}
                placeholder={'{ "booking_id": 124, "new_deadline": "2026-09-04" }'}
              />
            </Field>

            <div className="row">
              <Field label="Due — the recipient's deadline">
                <input type="datetime-local" value={dueAt} onChange={(e) => onDueChanged(e.target.value)} />
              </Field>
              <Field label="Expires — when it stops showing">
                <input type="datetime-local" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </Field>
            </div>

            {dueAt && expiresAt && (
              <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
                Between due and expiry the message shows as overdue.
              </p>
            )}

            {schema.data?.flags && schema.data.flags.length > 0 && (
              <div style={{ marginTop: 10 }}>
                <label>Flags — for display and filtering. They do not affect delivery.</label>
                <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 4 }}>
                  {schema.data.flags.map((f) => (
                    <div className="checkbox" key={f.key}>
                      <input
                        id={`fl-${f.key}`}
                        type="checkbox"
                        checked={flags.includes(f.key!)}
                        onChange={(e) =>
                          setFlags(e.target.checked ? [...flags, f.key!] : flags.filter((x) => x !== f.key))
                        }
                      />
                      <label htmlFor={`fl-${f.key}`}>{f.displayName}{f.isPlatform ? " (platform)" : ""}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: "flex", gap: 18, marginTop: 14 }}>
              <div className="checkbox">
                <input id="ack" type="checkbox" checked={requiresAck} onChange={(e) => setRequiresAck(e.target.checked)} />
                <label htmlFor="ack">requires acknowledgement</label>
              </div>
              <div className="checkbox">
                <input id="rep" type="checkbox" checked={allowsReply} onChange={(e) => setAllowsReply(e.target.checked)} />
                <label htmlFor="rep">allows replies</label>
              </div>
            </div>

            {requiresAck && (
              <div style={{ marginTop: 10 }}>
                <Alert kind="info">
                  The audience is recorded at publish, so read tracking stays accurate. Recipients
                  keep seeing this past its expiry, marked overdue, until they acknowledge it, and
                  cannot dismiss it before then.
                </Alert>
              </div>
            )}
          </Panel>
        </div>

        <div>
          <Panel title="Audience">
            {schema.data ? (
              <AudienceBuilder
                schema={schema.data}
                value={audience}
                onChange={setAudience}
                channels={channels}
                onChannelsChange={setChannels}
              />
            ) : (
              <p className="muted">Loading…</p>
            )}
          </Panel>

          <Panel title="Preview">
            <AudiencePreviewPanel audience={audience} channels={channels} />
          </Panel>

          <Panel>
            <button onClick={publish} disabled={!ready || busy} style={{ width: "100%" }}>
              {busy ? "Publishing…" : "Publish"}
            </button>
            <p className="muted" style={{ fontSize: 12, marginTop: 10, marginBottom: 0 }}>
              {channels.length > 0
                ? <>Only <Chip>{channels.join(", ")}</Chip> consumers will see this.</>
                : "Every channel a recipient holds will see this."}
            </p>
          </Panel>
        </div>
      </div>
    </>
  );
}
